import { isAdmin, type Session } from '@/lib/auth/session';
import type { Organization } from '@/features/organizer/api';
import { organizationStatus } from '@/features/organizer/vocab';

export interface Workspace {
  key: string;
  kind: 'operator' | 'organizer';
  name: string;
  href: string;
  status?: string;
}

export const OPERATIONS: Workspace = {
  key: 'ops',
  kind: 'operator',
  name: 'Catalog operations',
  href: '/ops',
};

export const NEW_ORGANIZATION = '/org/new';

export const organizationHref = (id: string): string => `/org/${id}`;

export const organizationWorkspace = (organization: Organization): Workspace => ({
  key: organization.id,
  kind: 'organizer',
  name: organization.name,
  href: organizationHref(organization.id),
  status: organizationStatus(organization),
});

/** Every workspace the person can open: operations for admins, then their organizations A–Z. */
export function workspacesFor(
  session: Session | null,
  organizations: readonly Organization[] = [],
): Workspace[] {
  return [...(isAdmin(session) ? [OPERATIONS] : []), ...organizations.map(organizationWorkspace)];
}

/**
 * Where "/" sends a signed-in person. Admins go to operations. Organizers go to the organization
 * they last opened, else the first approved one, else the first; with none, to create one.
 * `organizations` comes sorted A–Z.
 */
export function landingFor(
  session: Session | null,
  organizations: readonly Organization[] = [],
  remembered?: string | null,
): string {
  if (isAdmin(session)) return OPERATIONS.href;
  const pick =
    organizations.find((o) => o.id === remembered) ??
    organizations.find((o) => o.status === 'Approved') ??
    organizations[0];
  return pick ? organizationHref(pick.id) : NEW_ORGANIZATION;
}

const lastKey = (userId: string) => `rq-last-organization:${userId}`;

/** The organization this person last opened here, per browser. */
export function recallOrganization(session: Session | null): string | null {
  if (!session) return null;
  try {
    return localStorage.getItem(lastKey(session.user.userId));
  } catch {
    return null;
  }
}

export function rememberOrganization(session: Session | null, id: string): void {
  if (!session) return;
  try {
    localStorage.setItem(lastKey(session.user.userId), id);
  } catch {
    // Blocked storage: the landing falls back to the first organization.
  }
}

/** Only internal paths, so a crafted returnTo can't send someone off-site after sign-in. */
export function safeReturnTo(value: string | undefined): string | undefined {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : undefined;
}
